import React, { useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Plus, Trash2, Lightbulb, Mic, MicOff, StickyNote } from 'lucide-react';
import { Badge } from '../ui/badge';
import useDictation from '../../hooks/useDictation';

/**
 * Right column of the Sermon Prep tool — interactive outline builder.
 *
 * Voice dictation: a single global mic at the top of the panel listens
 * continuously and appends transcribed final chunks to the LAST FOCUSED
 * outline field (either a title input or a content textarea). If the user
 * hasn't focused anything yet, dictation appends to the first point's
 * content. A live "interim" preview chip surfaces what the recognizer is
 * still hearing. Browser-only (Web Speech API) — graceful no-op when the
 * SpeechRecognition API is unavailable.
 */
const SermonOutlinePanel = ({
  language,
  outline,
  onAddPoint,
  onRemovePoint,
  onUpdatePoint,
  onAddIllustration,    // (pointId, pointTitle) => void — opens illustrations dialog
  onInsertFromNotes,    // (pointId, pointTitle) => void — opens notes picker dialog
}) => {
  const { t } = useLanguage();
  // Track the most-recently-focused outline field so dictation knows where
  // to append. `{ pointId, field: 'title' | 'content' }`.
  const focusRef = useRef(null);
  const [focusBadge, setFocusBadge] = useState(null);

  const handleFocus = (pointId, field) => {
    focusRef.current = { pointId, field };
    const idx = outline.findIndex((p) => p.id === pointId);
    setFocusBadge({ idx: idx + 1, field });
  };

  const appendToTarget = (text) => {
    if (!text) return;
    let target = focusRef.current;
    // Default: first point's content.
    if (!target && outline.length > 0) {
      target = { pointId: outline[0].id, field: 'content' };
      const idx = 0;
      setFocusBadge({ idx: idx + 1, field: 'content' });
    }
    if (!target) return;
    const point = outline.find((p) => p.id === target.pointId);
    if (!point) return;
    const existing = point[target.field] || '';
    // Sentence-case the chunk and add a space (or newline if the existing
    // text ends with a period/exclam/question). The Web Speech API returns
    // lowercase chunks without trailing punctuation.
    const sep = !existing
      ? ''
      : (target.field === 'content' && /[.!?]\s*$/.test(existing) ? ' ' : (existing.endsWith(' ') ? '' : ' '));
    const next = existing + sep + text + (target.field === 'content' && !/[.!?]$/.test(text) ? '.' : '');
    onUpdatePoint(target.pointId, target.field, next);
  };

  const dictation = useDictation({
    language,
    onFinalChunk: appendToTarget,
  });

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg">
            <span className="text-sm sm:text-base">
              {t('sermon.sermonOutline')}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {dictation.supported && (
              <Button
                onClick={dictation.toggle}
                size="sm"
                variant={dictation.isListening ? 'default' : 'outline'}
                className={
                  dictation.isListening
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse touch-manipulation'
                    : 'text-purple-600 border-purple-600 hover:bg-purple-50 touch-manipulation'
                }
                style={{ minHeight: '36px' }}
                data-testid="outline-dictate-btn"
                title={t('sermon.dictateTitle')}
              >
                {dictation.isListening ? (
                  <>
                    <MicOff className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline text-xs">
                      {t('sermon.stop')}
                    </span>
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline text-xs">
                      {t('sermon.dictate')}
                    </span>
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={onAddPoint}
              size="sm"
              variant="outline"
              className="text-purple-600 border-purple-600 hover:bg-purple-50 touch-manipulation"
              style={{ minHeight: '36px', minWidth: '36px' }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Dictation status + interim preview */}
        {dictation.isListening && (
          <div
            className="mt-2 flex items-center gap-2 flex-wrap text-xs text-purple-700 dark:text-purple-300"
            data-testid="outline-dictate-status"
          >
            <Badge className="bg-red-500 hover:bg-red-500 text-white text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1" />
              {t('sermon.listening')}
            </Badge>
            {focusBadge && (
              <span className="opacity-90">
                {t('sermon.appendingTo')}{' '}
                <strong>
                  {`${t('sermon.point')} ${focusBadge.idx}`}
                  {' · '}
                  {focusBadge.field === 'title'
                    ? t('sermon.titleWord')
                    : t('sermon.contentWord')}
                </strong>
              </span>
            )}
            {dictation.interim && (
              <span
                className="italic opacity-70 truncate max-w-[200px]"
                data-testid="outline-dictate-interim"
              >
                “{dictation.interim}”
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <div className="space-y-3 sm:space-y-4">
          {outline.map((point, idx) => (
            <div key={point.id} className="space-y-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-base sm:text-lg font-bold text-purple-600 flex-shrink-0">
                  {idx + 1}.
                </span>
                <Input
                  value={point.title}
                  onChange={(e) => onUpdatePoint(point.id, 'title', e.target.value)}
                  onFocus={() => handleFocus(point.id, 'title')}
                  className="flex-1 font-semibold text-sm sm:text-base touch-manipulation"
                  placeholder={t('sermon.titlePlaceholder')}
                  style={{ minHeight: '40px' }}
                />
                {outline.length > 2 && (
                  <Button
                    onClick={() => onRemovePoint(point.id)}
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0 touch-manipulation"
                    style={{ minHeight: '36px', minWidth: '36px' }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <Textarea
                value={point.content}
                onChange={(e) => onUpdatePoint(point.id, 'content', e.target.value)}
                onFocus={() => handleFocus(point.id, 'content')}
                rows={3}
                placeholder={t('sermon.pointContentPlaceholder')}
                className="text-xs sm:text-sm touch-manipulation"
              />
              {(onAddIllustration || onInsertFromNotes) && (
                <div className="flex gap-2">
                  {onAddIllustration && (
                    <Button
                      onClick={() => onAddIllustration(point.id, point.title)}
                      size="sm"
                      variant="ghost"
                      className="flex-1 h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xs"
                      data-testid={`sermon-add-illustration-btn-${point.id}`}
                    >
                      <Lightbulb className="w-3.5 h-3.5 mr-1.5" />
                      {t('sermon.addIllustration')}
                    </Button>
                  )}
                  {onInsertFromNotes && (
                    <Button
                      onClick={() => onInsertFromNotes(point.id, point.title)}
                      size="sm"
                      variant="ghost"
                      className="flex-1 h-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 text-xs"
                      data-testid={`sermon-insert-notes-btn-${point.id}`}
                    >
                      <StickyNote className="w-3.5 h-3.5 mr-1.5" />
                      {t('sermon.insertFromNotes')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SermonOutlinePanel;
